import { DeviceId } from '@service-initiation/shared-clients-tech-care-services';
import { type Flow } from '@service-initiation/shared-data-access-flow-manager';
import { Route } from '@service-initiation/tech-care-data-access-routing';
import {
  DeliveryMethod,
  billingDetailsAtom,
  companyIdAtom,
  customerDetailsAtom,
  customerDetailsConfirmedAtom,
  damageTypePageConfirmedAtom,
  damageTypePageValidAsyncAtom,
  deliveryMethodAsyncAtom,
  deliveryMethodAtom,
  deliverySelectionConfirmedAtom,
  deliverySelectionPageValidAsyncAtom,
  deviceDetailsAsyncAtom,
  selectedAppointmentAsyncAtom,
  selectedDeviceTypeAsyncAtom,
  store,
} from '@service-initiation/tech-care-data-access-store';
import {
  AppointmentTimePage,
  completedAppointmentTime,
} from '@service-initiation/tech-care-features-appointment-time';
import {
  BillingDetailsPage,
  completedBillingDetails,
} from '@service-initiation/tech-care-features-billing-details';
import { ConfirmationPage } from '@service-initiation/tech-care-features-confirmation';
import {
  CustomerDetailsPage,
  completedCustomerDetails,
} from '@service-initiation/tech-care-features-customer-details';
import { DamageTypePage } from '@service-initiation/tech-care-features-damage-type';
import { DeliverySelectionPage } from '@service-initiation/tech-care-features-delivery-selection';
import {
  DeviceDetailsPage,
  completedDeviceDetails,
} from '@service-initiation/tech-care-features-device-details';
import {
  DeviceTypePage,
  deviceTypeCompleted,
} from '@service-initiation/tech-care-features-device-type';
import { Landing } from '@service-initiation/tech-care-features-landing';

export const defaultFlow: Flow<
  Exclude<Route, Route.CancelAppointment | Route.ConfirmCancel>
> = {
  [Route.AppointmentTime]: {
    completed: async () => {
      const appointment = await store.get(selectedAppointmentAsyncAtom);
      const deliveryMethod = await store.get(deliveryMethodAsyncAtom);
      return completedAppointmentTime(
        {
          date: appointment.date,
          endTime: appointment.endTime,
          intentId: appointment.intentId,
          isAsap: appointment.isAsap,
          startTime: appointment.startTime,
        },
        deliveryMethod
      );
    },
    component: <AppointmentTimePage />,
    next: () => Route.CustomerDetails,
  },
  [Route.BillingDetails]: {
    completed: () => {
      const billingDetails = store.get(billingDetailsAtom);
      return completedBillingDetails(billingDetails);
    },
    component: <BillingDetailsPage />,
    next: () => Route.Confirmation,
  },
  [Route.Confirmation]: {
    completed: () => false,
    component: <ConfirmationPage />,
    next: () => Route.Landing,
  },
  [Route.CustomerDetails]: {
    completed: () => {
      const customerDetails = store.get(customerDetailsAtom);
      const confirmed = store.get(customerDetailsConfirmedAtom);
      return (
        !!customerDetails &&
        confirmed &&
        completedCustomerDetails(customerDetails)
      );
    },
    component: <CustomerDetailsPage />,
    next: () => {
      const deliveryMethod = store.get(deliveryMethodAtom);
      return deliveryMethod === DeliveryMethod.ComeToMe
        ? Route.BillingDetails
        : Route.Confirmation;
    },
  },
  [Route.DamageType]: {
    completed: async () => {
      const valid = await store.get(damageTypePageValidAsyncAtom);
      const confirmed = store.get(damageTypePageConfirmedAtom);
      return valid && confirmed;
    },
    component: <DamageTypePage />,
    next: () => Route.DeliverySelection,
  },
  [Route.DeliverySelection]: {
    completed: async () => {
      const confirmed = store.get(deliverySelectionConfirmedAtom);
      const valid = await store.get(deliverySelectionPageValidAsyncAtom);

      return valid && confirmed;
    },
    component: <DeliverySelectionPage />,
    next: () => {
      const deliveryMethod = store.get(deliveryMethodAtom);
      return deliveryMethod === DeliveryMethod.MailIn
        ? Route.CustomerDetails
        : Route.AppointmentTime;
    },
  },
  [Route.DeviceDetails]: {
    completed: async () => {
      const deviceDetails = await store.get(deviceDetailsAsyncAtom);
      return completedDeviceDetails(deviceDetails);
    },
    component: <DeviceDetailsPage />,
    next: () => {
      const companyId = store.get(companyIdAtom);
      return companyId ? Route.DeliverySelection : Route.DamageType;
    },
  },
  [Route.DeviceType]: {
    completed: async () => {
      const deviceType = await store.get(selectedDeviceTypeAsyncAtom);
      return deviceTypeCompleted(deviceType);
    },
    component: <DeviceTypePage />,
    next: async () => {
      const companyId = store.get(companyIdAtom);
      const deviceDetails = await store.get(deviceDetailsAsyncAtom);
      const deviceType = await store.get(selectedDeviceTypeAsyncAtom);

      return deviceType?.id === DeviceId.Other &&
        deviceDetails?.id === DeviceId.OtherChild
        ? companyId
          ? Route.DeliverySelection
          : Route.DamageType
        : Route.DeviceDetails;
    },
  },
  [Route.Landing]: {
    completed: () => true,
    component: <Landing />,
    hidden: () => true,
    next: () => Route.DeviceType,
  },
};
